// Barra de filtros do Webhook Inbox, no padrão da Consulta do notification-center:
// campos rotulados numa faixa só, com o white label vindo do seletor GLOBAL do
// menu (useAuth.selectedWhiteLabel) — combo de loja aqui seria um segundo lugar
// para escolher a mesma coisa.
import { SearchIcon, Trash2Icon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { AppDateRangePicker } from "@/components/commons"
import { Button } from "@/components/ui/button"
import { ComboBox } from "@/components/ui/combo-box"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type {
  WebhookInboxFilters,
  WebhookInboxStatus,
} from "@/types/webhook-inbox"

import { WEBHOOK_INBOX_PROCESSORS } from "../catalog"
import {
  fromApiDate,
  toApiDate,
  todayRange,
} from "../date-range"
import { WEBHOOK_INBOX_FILTER_STATUSES } from "../status-config"

const filtersPrefix = "pages.adminWebhookInbox.filters"

/** Valor do Select para "sem filtro" — Radix não aceita SelectItem com value "". */
const ALL = "ALL"

const FilterField = ({
  id,
  label,
  className,
  children,
}: {
  id: string
  label: string
  className?: string
  children: React.ReactNode
}) => (
  <div
    className={cn(
      "flex min-w-40 flex-col gap-1",
      className
    )}
  >
    <Label
      htmlFor={id}
      className="text-xs text-muted-foreground"
    >
      {label}
    </Label>
    {children}
  </div>
)

interface WebhookInboxFiltersBarProps {
  filters: WebhookInboxFilters
  onChange: (filters: Partial<WebhookInboxFilters>) => void
  onClear: () => void
  onRefresh: () => void
}

export const WebhookInboxFiltersBar = ({
  filters,
  onChange,
  onClear,
  onRefresh,
}: WebhookInboxFiltersBarProps) => {
  const { t } = useTranslation()
  const [transactionTerm, setTransactionTerm] = useState(
    filters.transactionId ?? ""
  )
  // Ressincroniza quando o filtro muda por fora (limpar, deep-link) — padrão
  // "adjust state during render" do notification-center, sem useEffect.
  const [lastTransactionId, setLastTransactionId] =
    useState(filters.transactionId)
  if (filters.transactionId !== lastTransactionId) {
    setLastTransactionId(filters.transactionId)
    setTransactionTerm(filters.transactionId ?? "")
  }

  // "Todos os processadores" primeiro: e o que limpa o filtro (value vazio).
  const processorOptions = [
    {
      label: t(`${filtersPrefix}.allProcessors`),
      value: "",
    },
    ...WEBHOOK_INBOX_PROCESSORS.map((processor) => ({
      label: t(
        `pages.adminWebhookInbox.processors.${processor}`
      ),
      value: processor as string,
    })),
  ]

  const commitTransaction = () => {
    const trimmed = transactionTerm.trim()
    if (trimmed === (filters.transactionId ?? "")) return
    onChange({ transactionId: trimmed || undefined })
  }

  const { startDate: today } = todayRange()
  const hasFilters = Boolean(
    filters.status ||
    filters.processor ||
    filters.transactionId ||
    filters.startDate !== today ||
    filters.endDate !== today
  )

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
      <FilterField
        id="webhook-inbox-transaction"
        label={t(`${filtersPrefix}.transaction`)}
        className="min-w-64 flex-1"
      >
        <Input
          id="webhook-inbox-transaction"
          value={transactionTerm}
          onChange={(event) =>
            setTransactionTerm(event.target.value)
          }
          onBlur={commitTransaction}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              commitTransaction()
            }
          }}
          placeholder={t(
            `${filtersPrefix}.transactionPlaceholder`
          )}
        />
      </FilterField>

      <FilterField
        id="webhook-inbox-period"
        label={t(`${filtersPrefix}.period`)}
        className="min-w-64"
      >
        <AppDateRangePicker
          id="webhook-inbox-period"
          value={{
            from: fromApiDate(filters.startDate),
            to: fromApiDate(filters.endDate),
          }}
          onChange={(range) =>
            onChange({
              startDate: toApiDate(range.from),
              endDate: toApiDate(range.to),
            })
          }
        />
      </FilterField>

      <FilterField
        id="webhook-inbox-status"
        label={t(`${filtersPrefix}.status`)}
      >
        <Select
          value={filters.status ?? ALL}
          onValueChange={(value) =>
            onChange({
              status:
                value === ALL
                  ? undefined
                  : (value as WebhookInboxStatus),
            })
          }
        >
          <SelectTrigger id="webhook-inbox-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>
              {t(`${filtersPrefix}.allStatus`)}
            </SelectItem>
            {WEBHOOK_INBOX_FILTER_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {t(
                  `pages.adminWebhookInbox.status.${status}`
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField
        id="webhook-inbox-processor"
        label={t(`${filtersPrefix}.processor`)}
        className="min-w-56"
      >
        <ComboBox
          id="webhook-inbox-processor"
          items={processorOptions}
          selected={filters.processor ?? ""}
          onSelect={(value) =>
            onChange({ processor: value || undefined })
          }
          placeholder={t(`${filtersPrefix}.allProcessors`)}
          placeholderCommand={t(
            `${filtersPrefix}.searchProcessor`
          )}
          align="start"
        />
      </FilterField>

      <div className="flex items-center gap-2">
        <Button
          type="button"
          onClick={() => {
            commitTransaction()
            onRefresh()
          }}
        >
          <SearchIcon size={16} className="mr-2" />
          {t("common.search")}
        </Button>
        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            onClick={onClear}
          >
            <Trash2Icon size={16} className="mr-2" />
            {t("common.clear")}
          </Button>
        )}
      </div>
    </div>
  )
}
